import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink } from "react-router";
import Hero from "../../Components/Hero";
import SeoHead from "../../Components/SeoHead";
import ContactUs from "../../Components/Modals/ContactUs";
import {
  AudienceKey,
  getLocalizedAudienceContent,
} from "./content";
import { trackEvent } from "src/utils/analytics";
import {
  breadcrumbSchema,
  faqPageSchema,
  howToSchema,
  organizationSchema,
  serviceSchema,
  webPageSchema,
} from "src/utils/seoSchema";
import { useLocale } from "src/i18n/LocaleProvider";
import { homePath } from "src/i18n/paths";

type AudienceLandingProps = {
  audience: AudienceKey;
};

const sectionSx = {
  width: "100%",
  bgcolor: "#0b0f17",
  py: { xs: 6, md: 8 },
  px: { xs: 2, sm: 3, lg: 4 },
};

const innerSx = {
  maxWidth: 1280,
  mx: "auto",
  width: "100%",
};

const sectionHeadingSx = {
  color: "#fff",
  fontWeight: 700,
  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
};

const AudienceLanding: React.FC<AudienceLandingProps> = ({ audience }) => {
  const { locale, config } = useLocale();
  const content = getLocalizedAudienceContent(audience, locale);
  const related = content.related.map((key) =>
    getLocalizedAudienceContent(key, locale)
  );
  const contactSource = `audience_${audience}`;

  useEffect(() => {
    trackEvent("audience_landing_view", { audience, locale });
  }, [audience, locale]);

  return (
    <>
      <SeoHead
        title={content.seoTitle}
        description={content.seoDescription}
        keywords={content.keywords}
        canonical={content.path}
        ogTitle={content.seoTitle}
        ogDescription={content.seoDescription}
        jsonLd={[
          organizationSchema(),
          webPageSchema({
            name: content.seoTitle,
            description: content.seoDescription,
            url: content.path,
          }),
          serviceSchema({
            name: `STR Monitoring for ${content.audienceLabel}`,
            description: content.definition,
            audience: content.audienceLabel,
            url: content.path,
            areaServedCountry: config.countryName,
          }),
          howToSchema({
            name: content.practicalHeading,
            description: content.seoDescription,
            steps: content.practicalTips,
            url: content.path,
          }),
          faqPageSchema(content.faqs),
          breadcrumbSchema([
            { name: "Home", url: homePath(locale) },
            { name: content.audienceLabel, url: content.path },
          ]),
        ]}
      />

      <Box sx={{ width: "100%" }}>
        <Hero
          title={content.heroTitle}
          subTitle={content.heroSubtitle}
          showRadar
          chipLabel={content.heroChip}
          ctaLabel={content.ctaLabel}
          defaultMessage={content.defaultMessage}
          ctaHelperText={content.midCtaBody}
          analyticsSource={contactSource}
          audience={audience}
        />

        <Box sx={sectionSx}>
          <Box sx={innerSx}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 2 }}
            >
              What is STR monitoring for {content.audienceLabel.toLowerCase()}?
            </Typography>
            <Typography
              sx={{ color: "#cbd5e1", lineHeight: 1.8, maxWidth: 900 }}
            >
              {content.definition}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ ...sectionSx, bgcolor: "#070a10" }}>
          <Box sx={innerSx}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 2 }}
            >
              {content.introHeading}
            </Typography>
            <Typography
              sx={{ color: "#cbd5e1", lineHeight: 1.8, maxWidth: 900, mb: 4 }}
            >
              {content.introBody}
            </Typography>
            <Typography
              variant="h5"
              component="h3"
              sx={{ color: "#fff", fontWeight: 700, mb: 2 }}
            >
              {content.whoItsForHeading}
            </Typography>
            <List disablePadding>
              {content.whoItsFor.map((item) => (
                <ListItem key={item} alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#a78bfa" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      sx: { color: "#cbd5e1", lineHeight: 1.7 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={innerSx}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 3 }}
            >
              {content.painPointsHeading}
            </Typography>
            <List disablePadding>
              {content.painPoints.map((point) => (
                <ListItem key={point} alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#a78bfa" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={point}
                    primaryTypographyProps={{
                      sx: { color: "#cbd5e1", lineHeight: 1.7 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        <Box sx={{ ...sectionSx, bgcolor: "#070a10" }}>
          <Box sx={innerSx}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 4 }}
            >
              {content.howItHelpsHeading}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                },
                gap: 3,
              }}
            >
              {content.howItHelps.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid rgba(30, 41, 59, 1)",
                    bgcolor: "rgba(15, 23, 42, 0.7)",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "#fff", fontWeight: 700, mb: 1 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: "#94a3b8", lineHeight: 1.7 }}>
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={innerSx}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 4 }}
            >
              {content.comparisonHeading}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid rgba(30, 41, 59, 1)",
                  bgcolor: "rgba(15, 23, 42, 0.5)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#94a3b8", fontWeight: 700, mb: 2 }}
                >
                  {content.comparison.leftTitle}
                </Typography>
                <List disablePadding>
                  {content.comparison.leftItems.map((item) => (
                    <ListItem key={item} sx={{ px: 0, py: 0.75 }}>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          sx: { color: "#94a3b8", lineHeight: 1.6 },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Box
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid rgba(139, 92, 246, 0.35)",
                  bgcolor: "rgba(139, 92, 246, 0.08)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#d8b4fe", fontWeight: 700, mb: 2 }}
                >
                  {content.comparison.rightTitle}
                </Typography>
                <List disablePadding>
                  {content.comparison.rightItems.map((item) => (
                    <ListItem key={item} sx={{ px: 0, py: 0.75 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon sx={{ color: "#a78bfa" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          sx: { color: "#cbd5e1", lineHeight: 1.6 },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...sectionSx, bgcolor: "#070a10" }}>
          <Box
            sx={{
              ...innerSx,
              p: { xs: 3, md: 4 },
              borderRadius: "20px",
              border: "1px solid rgba(30, 41, 59, 1)",
              bgcolor: "rgba(15, 23, 42, 0.9)",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 2 }}
            >
              {content.midCtaHeading}
            </Typography>
            <Typography
              sx={{
                color: "#94a3b8",
                mb: 3,
                maxWidth: 720,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              {content.midCtaBody}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ContactUs
                buttonLabel={content.ctaLabel}
                buttonLabelMobile={content.ctaLabel}
                defaultMessage={content.defaultMessage}
                dialogTitle={content.ctaLabel}
                analyticsContext={{
                  source: `${contactSource}_mid`,
                  audience,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={innerSx}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 3 }}
            >
              {content.practicalHeading}
            </Typography>
            <Stack component="ol" spacing={2} sx={{ m: 0, pl: 0, listStyle: "none" }}>
              {content.practicalTips.map((tip, index) => (
                <Box
                  key={tip}
                  component="li"
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#a78bfa",
                      fontWeight: 800,
                      minWidth: 28,
                      fontFamily: "monospace",
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                  <Typography sx={{ color: "#cbd5e1", lineHeight: 1.7 }}>
                    {tip}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>

        <Box sx={{ ...sectionSx, bgcolor: "#070a10" }}>
          <Box sx={innerSx}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 3 }}
            >
              Frequently asked questions
            </Typography>
            {content.faqs.map((faq) => (
              <Accordion
                key={faq.question}
                disableGutters
                sx={{
                  bgcolor: "rgba(15, 23, 42, 0.9)",
                  color: "#fff",
                  border: "1px solid rgba(30, 41, 59, 1)",
                  mb: 1.5,
                  borderRadius: "12px !important",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#a78bfa" }} />}
                >
                  <Typography sx={{ fontWeight: 600, pr: 2 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: "#94a3b8", lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={innerSx}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ ...sectionHeadingSx, mb: 2 }}
            >
              {content.closingHeading}
            </Typography>
            <Typography
              sx={{ color: "#cbd5e1", lineHeight: 1.8, mb: 3, maxWidth: 900 }}
            >
              {content.closingBody}
            </Typography>
            <ContactUs
              buttonLabel={content.ctaLabel}
              buttonLabelMobile={content.ctaLabel}
              defaultMessage={content.defaultMessage}
              dialogTitle={content.ctaLabel}
              analyticsContext={{
                source: `${contactSource}_closing`,
                audience,
              }}
            />

            <Divider sx={{ my: 5, borderColor: "rgba(30, 41, 59, 1)" }} />

            <Typography variant="h6" sx={{ ...sectionHeadingSx, mb: 2 }}>
              Explore solutions for other audiences
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 3 }}
            >
              {related.map((item) => (
                <Button
                  key={item.key}
                  component={RouterLink}
                  to={item.path}
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(139, 92, 246, 0.5)",
                    color: "#d8b4fe",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#a78bfa",
                      bgcolor: "rgba(139, 92, 246, 0.1)",
                    },
                  }}
                >
                  {item.audienceLabel}
                </Button>
              ))}
              <Button
                component={RouterLink}
                to="/blog"
                variant="outlined"
                sx={{
                  borderColor: "rgba(148, 163, 184, 0.4)",
                  color: "#cbd5e1",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                STR compliance insights
              </Button>
              <Button
                component={RouterLink}
                to="/#features"
                variant="text"
                sx={{ color: "#94a3b8", textTransform: "none" }}
              >
                View all features
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AudienceLanding;
